import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ArticleCard from "@/components/feature/ArticleCard";
import SEOHead from "@/components/feature/SEOHead";
import { categories, articles } from "@/mocks/articles";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);

  if (!category) {
    return (
      <div className="min-h-screen bg-background-50 font-sans">
        <Navbar />
        <div className="pt-24 pb-20 text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-4">分类不存在</h1>
          <Link to="/" className="text-primary-600 hover:text-primary-700 font-label cursor-pointer">
            返回首页
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const categoryArticles = articles.filter((a) => a.category === slug);

  const categorySchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: `${category.name} - 51每日大赛`,
    description: category.description,
    url: `https://www.51meiridasai.com/category/${slug}`,
    inLanguage: "zh-CN",
    isPartOf: {
      "@type": "WebSite",
      name: "51每日大赛",
      url: "https://www.51meiridasai.com/",
    },
  };

  return (
    <>
      <SEOHead
        title={`${category.name} | 51每日大赛吃瓜爆料`}
        description={`51每日大赛${category.name}专栏 - ${category.description}实时更新51每日大赛今日吃瓜与每日大赛吃瓜爆料内容。`}
        keywords={`${category.name},每日大赛吃瓜爆料,51每日大赛今日,51每日大赛在线观看,每日大赛黑料网`}
        canonicalUrl={`https://www.51meiridasai.com/category/${slug}`}
        schema={categorySchema}
      />

      <div className="min-h-screen bg-background-50 font-sans">
        <Navbar />

        {/* Category Header */}
        <section className="pt-28 pb-10 bg-background-100">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="mb-4">
              <Breadcrumb
                items={[
                  { label: "首页", href: "/" },
                  { label: category.name },
                ]}
              />
            </div>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
              <div>
                <span className="text-xs font-label text-foreground-500 uppercase tracking-wide">
                  51吃瓜分类
                </span>
                <h1 className="text-3xl md:text-5xl font-heading font-bold text-foreground-900 mt-1">
                  {category.name}
                </h1>
                <p className="text-base text-foreground-600 mt-3 max-w-2xl leading-relaxed">
                  {category.description}
                </p>
                <p className="text-sm text-foreground-500 mt-2">
                  共 <strong className="text-foreground-700">{categoryArticles.length}</strong> 篇爆料文章
                </p>
              </div>
              <div className="flex items-center gap-2">
                <i className={`${category.icon} text-5xl text-primary-300`}></i>
              </div>
            </div>
          </div>
        </section>

        {/* Articles Grid */}
        <section className="py-10">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            {categoryArticles.length > 0 ? (
              <>
                {/* Featured first article */}
                {categoryArticles.length > 0 && (
                  <div className="mb-8">
                    <ArticleCard article={categoryArticles[0]} variant="featured" />
                  </div>
                )}

                {/* Rest in grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {categoryArticles.slice(1).map((article) => (
                    <ArticleCard key={article.slug} article={article} />
                  ))}
                </div>

                {/* No more articles fallback for single-article categories */}
                {categoryArticles.length === 1 && (
                  <div className="text-center py-16">
                    <p className="text-foreground-500 text-lg">更多精彩爆料正在路上...</p>
                    <Link
                      to="/"
                      className="mt-4 inline-block text-primary-600 hover:text-primary-700 font-label cursor-pointer"
                    >
                      返回首页查看更多
                    </Link>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-20">
                <i className="ri-inbox-line text-5xl text-foreground-300 mb-4 block"></i>
                <p className="text-foreground-500 text-lg">该分类暂无文章</p>
                <Link
                  to="/"
                  className="mt-4 inline-block px-6 py-2.5 bg-primary-500 text-background-50 font-label rounded-full hover:bg-primary-600 transition-colors cursor-pointer"
                >
                  返回首页
                </Link>
              </div>
            )}
          </div>
        </section>

        {/* Other Categories */}
        <section className="py-10 bg-background-100">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <h2 className="text-xl font-heading font-bold text-foreground-900 mb-6">
              探索更多分类
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {categories
                .filter((c) => c.slug !== slug)
                .map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="flex flex-col items-center gap-2 p-4 rounded-xl bg-background-50 border border-background-200 hover:border-primary-200 hover:bg-primary-50/50 transition-all duration-200 cursor-pointer group"
                  >
                    <div className="w-10 h-10 flex items-center justify-center">
                      <i className={`${cat.icon} text-xl text-primary-500 group-hover:text-primary-600 transition-colors`}></i>
                    </div>
                    <span className="text-sm font-label text-foreground-700 group-hover:text-primary-700 transition-colors whitespace-nowrap">
                      {cat.name}
                    </span>
                    <span className="text-xs text-foreground-500">{cat.count}篇</span>
                  </Link>
                ))}
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}